import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, ActivatedRouteSnapshot, Data, Router, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Observable } from 'rxjs';
import { delay, map, switchMap, tap } from 'rxjs/operators';

import { ApplicationBaseComponent } from '../../../applications/components/application-base.component';
import { ApplicationState, ApplicationStateChangeRequest, ApplicationType, InitiateWorkflowRequest, WorkflowData, WorkflowFieldFile, WorkflowNote } from '../../../applications/models/applications.models';
import { ApplicationNotesService } from '../../../applications/services/application-notes.service';
import { ApplicationsService } from '../../../applications/services/applications.service';
import { DomainDefinitionService } from '../../../builder/services/domain-definition.service';
import { OrganizationStub } from '../../../common/models/organization.model';
import { Well, WellLog } from '../../../common/models/well.model';
import { IUploadFile } from '../../../common/services/document-management.service';
import { IssuesService } from '../../../common/services/issues.service';
import { LoaderService } from '../../../common/services/loader.service';
import { SidePanelService } from '../../../common/services/side-panel.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { SubHeader } from '../../../core/models/sub-header.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { SubHeaderService } from '../../../core/services/sub-header.service';
import { DomainValue } from '../../../models/domain.model';
import { AuthService } from '../../../services/auth.service';
import { ComplianceReportingType } from '../../models/compliance.model';
import { WellSelectorComponent } from '../well-selector/well-selector.component';
import { OnBaseDocumentTypeProcessReference, OnBaseSubProcessName } from '../../../common/models/onBaseIntegrationAndMapping.model';


@Component({
    selector: 'app-electric-logs',
    templateUrl: './electric-logs.component.html',
    styleUrls: ['./electric-logs.component.scss'],
    standalone: false
})
export class ElectricLogsComponent extends ApplicationBaseComponent implements OnInit {

    @Input() logData: any;
    @Input() savedApp: WorkflowData;
    @Input() organization: OrganizationStub;
    @Input() reportType: ComplianceReportingType;
    @Input() applicationState: ApplicationState;
    @Input() onBaseDocumentTypes: OnBaseDocumentTypeProcessReference[] = [];
    
    storedFiles: IUploadFile[] = [];
    isInReview: boolean = false;
    onBehalf: boolean = false;
    wellIsSelected: boolean = false;
    typesOfLogs: DomainValue[] = [];
    loggingCompanies: DomainValue[] = [];

    get receivedDate(): Date {
        if (this.form == null) return null;
        let ctrl = this.form.get('receivedDate');
        if (ctrl == null) return null;
        let value: Date = (ctrl.value != null && ctrl.value != '') ? new Date(ctrl.value) : null;
        return value;
    }

    get uploadedFilesCtrl(): AbstractControl {
        if (this.form == null) return null;
        return <AbstractControl>this.form.get('uploadedFiles');
    }

    get uploadedFiles(): IUploadFile[] {
        if (this.uploadedFilesCtrl == null) return [];
        return <IUploadFile[]>this.uploadedFilesCtrl.value;
    }


    get noLogsChecked(): boolean {
        return this.form.get("logsRunCheck").value;
    }

    

    reviewStates: ApplicationState[] = [
        ApplicationState.VerifyCompletion,
        ApplicationState.InitialReview,
        ApplicationState.InitialApproval,
        ApplicationState.FinalApproval
    ];

    @ViewChild(WellSelectorComponent) private _selector: WellSelectorComponent;


    get filesUploaded(): boolean {
        let result: boolean = false;
        if (this.uploadedFiles.length == 0) {
            result = false;
        }

        else {
            result = true;
        }
        return result;
    }

    get isLoading(): boolean {
        return this.loading$.value;
    }

    loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    loadingObs$: Observable<boolean> = this.loading$.asObservable();

    get isInCorrections(): boolean {
        if (this.application == null) return false;
        return this.application.applicationState == ApplicationState.Corrections;
    }

    get selectedWell(): string {
        if (this.form == null) return "";
        let ctrl = this.form.get('apiNumber');
        if (ctrl == null) return "";
        return ctrl.value;
    }

    get enableWellSelection(): boolean {
        if (this.application == null) return true;
        else {
            if (this.isApproved) return false;
            if (this.isInternal && this.isInCorrections) return false;
            if (this.getFilteredNotes('apiNumber').length != 0 && this.isInCorrections) return true;
            if (this.application.applicationState == ApplicationState.Input || this.application.applicationState == ApplicationState.Start) return true;
            return false;
        }
    }

    get isInternal(): boolean {
        return this.auth.User.isInternal;
    }

    get isApproved(): boolean {
        if (this.application == null) return null;
        if (this.application.applicationState == ApplicationState.Approved) return true;
        return false;
    }

    get subheaderText(): string {
        return "Compliance Report #[XXX]";
    }

    subheader: SubHeader = new SubHeader({ title: "", buttons: [] });


    submittedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been submitted for review.", [new DialogButton("OK", true, () => { this.home(); })]);
    static saveUnsubmittedMessage: string = "Your application has been saved for completion at a later date.  For future reference, this report is stored as ID [XXX].";
    saveUnsubmittedDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () => { this.navigateToSavedReport(); return true; })]);
    canceledDialog: Dialog = new Dialog("Leave application unsaved and return to home page?", [new DialogButton("Yes", true, () => { this.router.navigate(["/home"]); }), new DialogButton("No", true, () => { }),]);

    acceptedDialog: Dialog = new Dialog("Confirm that you are accepting this application?", [new DialogButton("Confirm", true, () => { this.onAcceptConfirmed(); }), new DialogButton("Cancel", true, () => { }),]);
    acceptConfirmedDialog: Dialog = new Dialog("The application has been accepted.", [new DialogButton("OK", true, () => { this.uploadDocumentsAndGoHome(); })]);


    constructor(private activatedRoute: ActivatedRoute, private builder: FormBuilder, public loader: LoaderService, public dialogService: DynamicDialogService, _subheader: SubHeaderService, _auth: AuthService, public appService: ApplicationsService,
        _sidePanelService: SidePanelService, _notesService: ApplicationNotesService, _router: Router, public domainService: DomainDefinitionService, public issuesService: IssuesService) {
        super(loader, dialogService, _subheader, _auth, appService, _router, _sidePanelService, _notesService);

        this.trueBooleanFields = ['confidentiality', 'logsRunCheck'];
        this.fileFields = ['uploadedFiles'];
        this.dateFields =['receivedDate']

    }

    ngOnInit() {

        this.form = this.buildForm();

        let loadingObs$ = this.loadingObs$.pipe(
            delay(1000)
        ).subscribe(
            (isLoading: boolean) => {
                if (isLoading == false) this.restoreWells();
            },
            (error) => {
                this.handleError(error);
            });

        this.subscription.add(loadingObs$);

        if (this.savedApp != null) {
            this.application = this.savedApp;
            this.form = this.restoreFormData(this.form);
            this.subheader.title = this.subheaderText.replace("[XXX]", this.application.applicationNumber);
            if (this.application.applicationState != ApplicationState.Approved && this.isInternal) {
            this.subheader.buttons.push({ name: "View Notes & Timeline", method: () => this.viewTimeline() });

            }
            this.subheaderService.updateSubHeader(this.subheader);

            this.loading$.next(false);
            this.applicationState = this.application.applicationState;
            this.wellIsSelected = true;

            if (this.application.applicationState == ApplicationState.InitialReview || this.application.applicationState == ApplicationState.Approved) {
                this.isInReview = true;
                this.form.disable();

            }
            else if (this.application.applicationState == ApplicationState.Corrections) {
                this.isInReview = false;
                this.form.enable();

            }


            this.form = this.restoreFormData(this.form);

    
          
            for (let i = 0; i < this.application.workflowFieldDatas.find(f => f.formControlName == 'uploadedFiles').workflowFieldFiles.length; i++) {

                this.storedFiles.push(this.convertWorkflowFieldFileToIUploadFile(this.application.workflowFieldDatas.find(f => f.formControlName == 'uploadedFiles').workflowFieldFiles[i]))
            }

        
        }

        

        const types$: Observable<DomainValue[]> = this.domainService.getValues("LOGS").pipe(
            tap((data: DomainValue[]) => {
                this.typesOfLogs = data;
                this.typesOfLogs.unshift({
                    Code: "", Definition: ""
                } as DomainValue);
            })

        );
        const tSubs$: Subscription = types$.subscribe(
            () => { },
            (error) => {
                console.error(error);
            }
        );
        this.subscription.add(tSubs$);
       

        const cos$: Observable<DomainValue[]> = this.domainService.getValues("LOG_CO").pipe(
            tap((data: DomainValue[]) => {
                this.loggingCompanies = data;
                this.loggingCompanies.unshift({
                    Code: "", Definition: ""
                } as DomainValue);
            })

        );
        const cSubs$: Subscription = cos$.subscribe(
            () => { },
            (error) => {
                console.error(error);
            }
        );
        this.subscription.add(cSubs$);

   }

    ngOnDestroy() {
        super.ngOnDestroy();
        if (this.subscription) this.subscription.unsubscribe();
    }



    buildForm(): FormGroup {
        return this.builder.group({
            wellName: ["", []],
            wellNumber: [null, []],
            confidentiality: [false, []],
            logsRunCheck: [false, []],
            typeOfLog: ["", []],
            loggingCompany: ["", []],
            uploadedFiles: [[], []],
            organization: [this.organization.id, []],
            organizationName: [this.organization.name, []],
            rbdmsNumber: [this.organization.historicalOwnerNumber, []],
            apiNumber: ["", []],
            reportType: [this.reportType.typeId, []],
            receivedDate: [null, []],


        })

    }

    onNoLogsChecked(event: Event) {
        this.form.get("logsRunCheck").setValue(event.target['checked']);
    
    }

    onConfidentialityChecked(event: Event) {
        this.form.get("confidentiality").setValue(event.target['checked']);
    
    }

  

    fileAdded(evt: IUploadFile): void {
        const filesCtrl = this.form.get("uploadedFiles");
        if (filesCtrl != null) {
            const filesList: IUploadFile[] = <IUploadFile[]>filesCtrl.value;
            const isNew = filesList.findIndex(l => l.fileName === evt.fileName) === -1;
            if (isNew) {
                evt.documentType = this.onBaseDocumentTypes.find(t => t.subProcessName == OnBaseSubProcessName.ElectricLogs)?.onBaseDocumentTypeName;
                filesList.push(evt);
                filesCtrl.setValue(filesList);
                this.storedFiles.push(evt);

            }
        }
    }

    onFileDeleted(evt: IUploadFile): void {
        const filesCtrl = this.form.get("uploadedFiles");
        if (filesCtrl != null) {
            const filesList: IUploadFile[] = <IUploadFile[]>filesCtrl.value;
            const fileIndex = filesList.findIndex(l => l.fileName === evt.fileName);
            if (fileIndex !== -1) {
                filesList.splice(fileIndex, 1);
                filesCtrl.setValue(filesList);
                this.storedFiles.splice(fileIndex, 1);
            }
        }
   
    }


    onWellsSelected(evt: Well[]): void {
        //should only ever receive one well, but selector emits a collection so handle as a collection
        let selectedWell = this.form.get('apiNumber');
    
        if (evt.length != 0) {

        let wellName: string = evt[0].name;
        if (evt[0].lease != null && evt[0].lease.name != null && evt[0].lease.name.length > 0) wellName = evt[0].lease.name;
        else if (evt[0].wellNumber != null && evt[0].wellNumber.length > 0 && wellName != null && wellName.trim().endsWith(evt[0].wellNumber.trim())) {
            wellName = wellName.trim().replace(evt[0].wellNumber.trim(), "");
        }

        this.form.get('wellNumber').setValue(evt[0].wellNumber);
        this.form.get('wellName').setValue(wellName);
        }

        let update: boolean = false;
        evt.forEach(w => {
            if (w.id != selectedWell.value) {
                this.form.get('apiNumber').setValue(w.id);

                update = true;
            }
        });
        if (update) {
            if (evt.length == 0) {
                this.form.get('apiNumber').setValue(null);
            }
        }


        if (evt.length > 0) {
            this.wellIsSelected = true;

        }
        else if (evt.length == 0) {
            this.wellIsSelected = false;
        }
    }

    restoreWells(): void {
        if (this.application != null) {
            if (this._selector != null) {
                this._selector.reportedWells = [{ apiNumber: this.selectedWell, reviewStatus: "Pending" }];
                this._selector.findWells();
            }
            else {
                console.error("no selector component ready");
            }
        }
    }



    buildRequest(behalfFlag: boolean): InitiateWorkflowRequest {
        return {
            organizationId: this.organization.id,
            applicationType: ApplicationType.ComplianceReporting,
            onBehalf: behalfFlag,
            reportType: "LOGD"
        } as InitiateWorkflowRequest
    }

    checkDataBeforeSave() {

        this.form.get('reportType').setValue("LOGD");

        if (this.noLogsChecked) {
            this.logData.logCompany = "";
            this.logData.logType = "";
        }
        else {
            this.logData.logCompany = this.form.get("loggingCompany").value;
            this.logData.logType = this.form.get("typeOfLog").value;
        }

        if (this.application != null) {
            this.form.get('rbdmsNumber').setValue(this.application.workflowFieldDatas.find(f => f.formControlName == "rbdmsNumber").stringData);
            this.form.get('organizationName').setValue(this.application.workflowFieldDatas.find(f => f.formControlName == "organizationName").stringData);
        }

        if (this.auth.User.isInternal == true) {
            this.onBehalf = true;
        }

    }


    saveForLater() {

        this.checkDataBeforeSave();

        let obs$: Observable<WorkflowData> = (this.application == null) ? this.appService.initiateApplication(this.buildRequest(this.onBehalf)).pipe(switchMap((app) => {
            this.application = app; return this.saveProgress();
        })) : this.saveProgress();
        this.loader.toggleLoader(true);

        let subs$ = obs$.pipe()
            .subscribe(
                (app) => {
                    this._loader.toggleLoader(false);
                    this.logData.id = app.applicationNumber;
                    const dialog: Dialog = this.saveUnsubmittedDialog;
                  
                    dialog.message = ElectricLogsComponent.saveUnsubmittedMessage.replace("[XXX]", app.applicationNumber);
                    dialog.toggleDialog(true);
                    this.dialogService.updateDialog(dialog);
                    this.form.markAsPristine();

                    this.allowDeactivate.next(true);
                },
                (error) => {
                    this.logError(error);
                }
            );

        this.subscription.add(subs$);

    }

    submit() {

        this.checkDataBeforeSave();

        let obs$: Observable<WorkflowData> = (this.application == null) ? this.appService.initiateApplication(this.buildRequest(this.onBehalf)).pipe(switchMap((app) => {
            this.application = app; return this.saveProgress().pipe(
                switchMap((app: WorkflowData) => {
                    let destination: ApplicationState = ApplicationState.InitialReview;

                    let request: ApplicationStateChangeRequest = {
                        applicationId: app.id,
                        currentState: app.applicationState,
                        requestedState: destination
                    } as ApplicationStateChangeRequest;
                    return this.dataService.routeApplication(request);
                })
            );
        })) : this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.InitialReview;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );;

        this.loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.submittedDialog.message = this.submittedDialog.message.replace('#[APPNUMBER]', app.applicationNumber);
                    this.loader.toggleLoader(false);
                    this.submittedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.submittedDialog);
                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

        );
        this.subscription.add(subs$);
    }

    navigateToSavedReport() {
        this.router.navigate([`/issues/${this.logData.id}/resume`]);
    }

    saveConfirmed(exit: boolean): void {
        if (exit) { this.router.navigate(["/home"]); }
    }

    cancel() {
        this.canceledDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.canceledDialog);
    }

    setDates(): void {
        this.form.get('receivedDate').setValue(this.receivedDate);
    }


    onAccept() {
        this.setDates()
        this.acceptedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.acceptedDialog);
    }

    onAcceptConfirmed() {


         this.checkDataBeforeSave();

        let obs$: Observable<WorkflowData> = (this.application == null) ? this.appService.initiateApplication(this.buildRequest(this.onBehalf)).pipe(switchMap((app) => {
            this.application = app; return this.saveProgress().pipe(
                switchMap((app: WorkflowData) => {
                    let destination: ApplicationState = ApplicationState.Approved;

                    let request: ApplicationStateChangeRequest = {
                        applicationId: app.id,
                        currentState: app.applicationState,
                        requestedState: destination
                    } as ApplicationStateChangeRequest;
                    return this.dataService.routeApplication(request);
                })
            );
        })) : this.saveProgress().pipe(
            switchMap((app: WorkflowData) => {
                let destination: ApplicationState = ApplicationState.Approved;

                let request: ApplicationStateChangeRequest = {
                    applicationId: app.id,
                    currentState: app.applicationState,
                    requestedState: destination
                } as ApplicationStateChangeRequest;
                return this.dataService.routeApplication(request);
            })
        );;

        this.loader.toggleLoader(true);
        let subs$ = obs$.pipe()
            .subscribe(
                (app: WorkflowData) => {
                    this.loader.toggleLoader(false);
                    this.acceptConfirmedDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.acceptConfirmedDialog);
                },
                (error) => {
                    console.error(error);
                    this._loader.toggleLoader(false);
                    this.dialogService.showError();
                }

        );
        this.subscription.add(subs$);
    }


    logError(error: any): void {
        console.error(error);
        this.loader.toggleLoader(false);
        this.dialogService.showError();
    }

    hasFieldNotes(fieldName: string): boolean {
        return this.getFilteredNotes(fieldName).length > 0;
    }
   

    uploadDocumentsAndGoHome(): void {
        let upload$: Subscription = this.dataService.commitAssociatedDocuments(this.application.applicationNumber, false).pipe()
            .subscribe(
                () => { },
                (error) => {
                    this.handleError(error);
                }
            );
        this.subscription.add(upload$);
        setTimeout(() => { this.home(); }, 1500); //timeout before redirecting to give subscription a chance to fire
    }

    onDateUpdated(date: Date, field: string): void {
        let ctrl = this.form.get(field);
        if (ctrl != null) ctrl.setValue(date);
    }
  
}
